<template>
  <div class="page-template page" :class="pageCssClass">
    <section class="page-main">
      <!-- Need to add wrapper as a workaround for sticky bug in iOS -->
      <!-- Reference: https://stackoverflow.com/a/57938266 -->

      <!-- Attached toolbar need to be separate from content to position correctly -->
      <div v-if="hasAttachedToolbar" class="attached-toolbar-wrapper">
        <slot name="attached-toolbar" />
      </div>

      <overflow-menu-button
        v-if="isLogged && showHelpButton && !hasSide"
        class="btn-helpdesk-ticket"
        :aria-label="t('help_button_aria_label')"
        up
        disable-keyboard-nav
      >
        <svg-icon name="Help24" kind="action" />
        <span class="label">{{ t("help_button_label") }}</span>
        <template #overflow-menu>
          <overflow-menu-item @click="handleRedirectToUserGuide">
            {{ t("help_button_user_guide_label") }}
          </overflow-menu-item>
          <overflow-menu-item @click="handleRedirectToHelpdesk">
            {{ t("help_button_contact_support_label") }}
          </overflow-menu-item>
        </template>
      </overflow-menu-button>

      <back-to-top-button
        v-if="showBackToTopButton && !hideBackToTopButton && !hasSide"
        tabindex="-1"
      >
        <template #content>
          <svg-icon name="ArrowDirectionUp24" kind="action" />
          <span>{{ t("back_to_top_button_label") }}</span>
        </template>
      </back-to-top-button>

      <div class="main-wrapper">
        <div v-if="hasSide" class="content-floating-icons">
          <overflow-menu-button
            v-if="isLogged && showHelpButton"
            class="btn-helpdesk-ticket"
            :aria-label="t('help_button_aria_label')"
            up
            disable-keyboard-nav
          >
            <svg-icon name="Help24" kind="action" />
            <span class="label">{{ t("help_button_label") }}</span>
            <template #overflow-menu>
              <overflow-menu-item @click="handleRedirectToUserGuide">
                {{ t("help_button_user_guide_label") }}
              </overflow-menu-item>
              <overflow-menu-item @click="handleRedirectToHelpdesk">
                {{ t("help_button_contact_support_label") }}
              </overflow-menu-item>
            </template>
          </overflow-menu-button>

          <back-to-top-button v-if="showBackToTopButton && !hideBackToTopButton">
            <template #content>
              <svg-icon name="ArrowDirectionUp24" kind="action" />
              <span>{{ t("back_to_top_button_label") }}</span>
            </template>
          </back-to-top-button>
        </div>

        <div v-if="hasHeader" class="header">
          <slot name="image" />
          <div class="header-wrapper">
            <h3 v-if="hasTitle" class="title">
              <svg-icon v-if="hasTitleIcon" :name="titleIcon" kind="indicative" />
              <span>{{ title }}</span>
            </h3>
            <h4 v-if="hasSubTitle" class="sub-title">{{ subTitle }}</h4>
            <slot name="header" />
          </div>
        </div>

        <div v-if="hasSplitLayout" class="layout-split">
          <slot name="message-banner" />
          <div v-if="slots['split-side']" class="layout-split-side">
            <slot name="split-side" />
          </div>
          <div class="layout-split-main" :class="splitMainClass">
            <slot name="split-main" />
          </div>
        </div>

        <div v-else class="content">
          <slot name="content" />
        </div>

        <slot name="content-sticky" />
        <slot name="popup" />

        <div v-if="hasNav" class="page-nav">
          <slot name="nav" />
        </div>
      </div>
    </section>
    <section v-if="hasSide" class="page-side">
      <slot name="side" />
    </section>
  </div>
</template>

<script setup lang="ts">
import { isEmpty } from "@utils/var.check.ts";
import DOMUtils from "@utils/dom.utils.ts";
import DeviceUtils from "@utils/device.utils.ts";
import { nextTick, useSlots } from "vue";
import { useStore } from "vuex";
import { APPEARANCE_SETTINGS_VIEW, DRAWER_NAME } from "@modules/app/service/app.constant.ts";
import BackToTopButton from "@plugins/common/components/BackToTopButton.vue";
import appConfigs from "@configs/app.ts";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import OverflowMenuButton from "@plugins/common/components/OverflowMenuButton.vue";
import OverflowMenuItem from "@plugins/common/components/OverflowMenuItem.vue";
import { ref, computed, watch, onMounted, onBeforeUpdate } from "vue";
import { useVm } from "@utils/vm.ts";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";

// Props
interface Props {
  type?: string;
  condensed?: boolean;
  comfortRead?: boolean;
  title?: string;
  titleIcon?: string;
  subTitle?: string;
  showHelpButton?: boolean;
  fullWidth?: boolean;
  headerBanner?: boolean;
  isBrowsePage?: boolean;
  hideBackToTopButton?: boolean;
  splitMainKind?: string;
}

const {
  type = "content",
  condensed = false,
  comfortRead = false,
  headerBanner = false,
  title = "",
  titleIcon = "",
  subTitle = "",
  showHelpButton = true,
  fullWidth = false,
  hideBackToTopButton = false,
  splitMainKind = "",
  isBrowsePage = false,
} = defineProps<Props>();

const { vm } = useVm();
const { t } = useI18n();
const store = useStore();
const slots = useSlots();
const router = useRouter();

// Data
const hasNav = ref(!!slots["nav"]);
const hasSide = ref(!!slots["side"]);
const pageMainWidth = ref(0);
const pageNavHidden = ref(true);
const showBackToTopButton = ref(false);
const isLeftPanelPinned = ref(false);
const scrollThreshold = ref(0);

// Computed
const showLeftPanel = computed(() => store.getters["app/showLeftPanel"]);
const getPageDrawerExpanded = computed(() => store.getters["app/getPageDrawerExpanded"]);
const getAppearanceSettingsWidth = computed(() => store.getters["app/getAppearanceSettingsWidth"]);
const getNotebookAppearanceSettingsWidth = computed(
  () => store.getters["app/getNotebookAppearanceSettingsWidth"],
);
const isTeacher = computed(() => store.getters["auth/isTeacher"]);
const isStudent = computed(() => store.getters["auth/isStudent"]);
const isLogged = computed(() => store.getters["auth/isLogged"]);

const pageCssClass = computed(() => {
  return [
    {
      _condensed: condensed,
      _comfort: comfortRead,
      "header-banner": headerBanner,
      "full-width": fullWidth,
      "has-side": hasSide.value,
      "has-page-nav": hasNav.value,
      "has-split-layout": hasSplitLayout.value,
      "page-nav-hidden": pageNavHidden.value,
      "standard-width": !isWideWidth.value || isMobileView.value,
      "notebook-standard-width": !isNotebookWideWidth.value || isMobileView.value,
      "left-panel-pinned": isLeftPanelPinned.value,
      "is-browse-page": isBrowsePage,
    },
    `${type}-page`,
  ];
});

const hasAttachedToolbar = computed(() => {
  return !!slots["attached-toolbar"];
});

const hasHeader = computed(() => {
  return !!slots["header"] || title || subTitle;
});

const hasTitle = computed(() => {
  return !isEmpty(title);
});

const hasSubTitle = computed(() => {
  return !isEmpty(subTitle);
});

const hasTitleIcon = computed(() => {
  return !isEmpty(titleIcon);
});

const isPageDrawerExpanded = computed(() => {
  return (
    getPageDrawerExpanded.value(DRAWER_NAME.ATTEMPT) ||
    getPageDrawerExpanded.value(DRAWER_NAME.SETTINGS)
  );
});

const isWideWidth = computed(() => {
  return getAppearanceSettingsWidth.value === APPEARANCE_SETTINGS_VIEW.WIDE;
});

const isNotebookWideWidth = computed(() => {
  return getNotebookAppearanceSettingsWidth.value === APPEARANCE_SETTINGS_VIEW.WIDE;
});

const isMobileView = computed(() => {
  return DeviceUtils.isMobile();
});

const hasSplitLayout = computed(() => !!slots["split-main"]);

const splitMainClass = computed(() => ({
  [`layout-split-main-${splitMainKind}`]: splitMainKind,
}));

// Methods
const updatePageTemplateWidthCSSVariable = () => {
  const pageMain = DOMUtils.el(".page-main");
  DOMUtils.setCSSVariable("--pagewidth", `${vm.$el.clientWidth}px`);

  if (pageMain) {
    DOMUtils.setCSSVariable("--page-main-width", `${pageMain.clientWidth}px`);
    pageMainWidth.value = pageMain.clientWidth;
  }
};

const updateViewportHeightCSSVariable = () => {
  DOMUtils.setCSSVariable("--vh", `${window.innerHeight * 0.01}px`);
};

const resizeEvent = () => {
  updatePageTemplateWidthCSSVariable();
  updateViewportHeightCSSVariable();
  calculateScrollThreshold();
  updateBackToTopButtonVisibility();
};

const checkSlots = () => {
  nextTick(() => {
    hasNav.value = !!slots["nav"];
    hasSide.value = !!slots["side"];
  });
};

const updateBackToTopButtonVisibility = () => {
  showBackToTopButton.value = window.scrollY > scrollThreshold.value;
};

const calculateScrollThreshold = () => {
  const windowHeight = window.innerHeight;
  const fullHeight = document.documentElement.scrollHeight;
  scrollThreshold.value = (fullHeight - windowHeight) * 0.3;
};

const handleScroll = () => {
  updateBackToTopButtonVisibility();
};

const updatePageMainWidth = () => {
  // There is animation delay for the drawer to expand/collapse, hence the need for a Timeout
  // Duration timing: $duration--moderate-02 - 240ms
  setTimeout(() => {
    const pageMain = DOMUtils.elOf(vm.$el, ".page-main")[0];
    DOMUtils.setCSSVariable("--page-main-width", `${pageMain.clientWidth}px`);
  }, 240);
};

const observePageTemplate = () => {
  const mutationObserver = new MutationObserver((mutationsList, observer) => {
    for (const mutation of mutationsList) {
      if (
        mutation.type === "childList" &&
        mutation.addedNodes.length > 0 &&
        DOMUtils.el(".page-main")
      ) {
        setTimeout(() => {
          updatePageTemplateWidthCSSVariable();
          updateViewportHeightCSSVariable();
          pageNavHidden.value = false;
        }, 240);
        observer.disconnect(); // Stop observing after .page-main found
        break;
      }
    }
  });

  mutationObserver.observe(document.body, { childList: true, subtree: true });
};

const updateLeftPanelPinState = () => {
  const previousLeftPanelPinState = isLeftPanelPinned.value;

  isLeftPanelPinned.value =
    showLeftPanel.value && !document.querySelector("header")?.classList.contains("hide-side-nav");

  if (isLeftPanelPinned.value !== previousLeftPanelPinState) {
    updatePageMainWidth();
  }
};

const handleRedirectToUserGuide = () => {
  const userGuideUrl = isTeacher.value
    ? appConfigs.teacherUserGuideUrl
    : isStudent.value
      ? appConfigs.studentUserGuideUrl
      : appConfigs.userGuideUrl;

  window.open(userGuideUrl, "_blank");
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const handleRedirectToHelpdesk = () => {
  const routeData = router.resolve({
    name: "HelpdeskTicket",
  });

  window.open(routeData.href, "_blank");
};

// Watch
watch(showLeftPanel, () => {
  updateLeftPanelPinState();
});

watch(isPageDrawerExpanded, () => {
  updatePageMainWidth();
});

// Mounted
onMounted(() => {
  updateLeftPanelPinState();
  Promise.all([
    store.dispatch("app/loadAppearanceSettingsWidth"),
    store.dispatch("app/loadNotebookAppearanceSettingsWidth"),
  ]).then(() => {
    observePageTemplate();
    checkSlots();
    calculateScrollThreshold();

    window.addEventListener("scroll", handleScroll, { passive: true });

    updateBackToTopButtonVisibility();

    nextTick(() => {
      window.addEventListener("resize", resizeEvent);
    });
  });
});

// BeforeUpdate
onBeforeUpdate(() => {
  checkSlots();
  updateLeftPanelPinState();
});
</script>

<style lang="scss">
@use "@styles/abstracts/functions" as *;
@use "@styles/abstracts/mixins" as *;
@use "@styles/layout/breakpoint" as *;
@use "@styles/layout/z-map" as *;
@use "@styles/themes/sls-theme/tokens/colors" as *;
@use "@styles/themes/sls-theme/tokens/effects" as *;
@use "@styles/themes/sls-theme/tokens/spacing" as *;
@use "@styles/themes/sls-theme/tokens/typo" as *;
@use "@styles/abstracts/variables" as *;
@use "carbon-components/scss/globals/scss/motion.scss" as *;

.page {
  height: 100%;
  .main-wrapper {
    > .header {
      &:has(.search-banner) {
        @include media("desktop") {
          position: sticky;
          top: -0.5rem;
        }
      }
    }
  }

  &.is-browse-page {
    .main-wrapper {
      > .header {
        &:has(.search-banner) {
          @include media("desktop") {
            top: 3rem;
          }
        }
      }
    }
  }
  &.content-page {
    &.header-banner {
      .page-main {
        .main-wrapper {
          > .header {
            margin: 0 0 $spacing-05 0;
            width: 100%;
            z-index: calc(z-map("default") + 4);
          }
          > .content {
            > .bx--btn {
              display: flex;
              justify-self: flex-end;
              margin-bottom: $spacing-05;
              margin-left: $layout-01;
              margin-right: $layout-01;

              @include media("tablet-min") {
                margin-left: $layout-03;
                margin-right: $layout-03;
              }

              @include media("desktop") {
                margin-left: $layout-05;
                margin-right: $layout-05;
              }
            }

            > .bx--btn-set {
              justify-content: flex-end;
              gap: $spacing-04;
              flex-flow: wrap;
              margin-bottom: $spacing-05;
              margin-left: $layout-01;
              margin-right: $layout-01;

              .bx--btn {
                &:not(.btn-switch-account) {
                  max-width: none;
                }
              }

              @include media("tablet-min") {
                margin-left: $layout-03;
                margin-right: $layout-03;
              }

              @include media("desktop") {
                margin-left: $layout-05;
                margin-right: $layout-05;
              }
            }
          }
        }
      }
    }
  }

  &.content-page {
    min-height: inherit;

    .page-main {
      .main-wrapper {
        margin-left: auto;
        margin-right: auto;

        @include media("mobile") {
          width: calc(100% - #{$layout-01} - #{$layout-01});
        }

        @include media("tablet") {
          width: calc(100% - #{$layout-03} - #{$layout-03});
        }

        @include media("desktop") {
          width: calc(100% - #{$layout-05} - #{$layout-05});
        }

        > .content {
          margin-top: $layout-03;
          width: 100%;

          @include media("mobile") {
            margin-top: $spacing-05;
          }
        }
      }
    }

    &.full-width {
      .page-main {
        .main-wrapper {
          width: 100%;
          @include media("tablet") {
            padding-left: 54px;
          }
        }
      }
    }
  }

  &:not(.left-panel-pinned) {
    @include media("desktop-large") {
      .page-nav {
        width: calc(var(--page-main-width));
      }
    }
  }

  .page-nav {
    bottom: 0.5rem;
    z-index: z-map("page-nav");
    width: var(--page-main-width);
    @include media("desktop") {
      padding-left: $collapsed-panel-width;
    }
    @include media("desktop-large") {
      padding-left: 0;
    }

    .page-nav-background {
      max-width: calc(100% - 9.5rem);
      height: inherit;
      border-radius: 60px;

      @include media("tablet-min") {
        width: $page-nav-tablet-width;
      }

      @include media("desktop") {
        width: $page-nav-desktop-width;
      }

      @media screen and (min-width: 1920px) {
        width: $page-nav-large-desktop-width;
      }
    }

    .page-nav-wrapper {
      height: inherit;
      display: grid;
      grid-template-columns: 1fr 220px 1fr;
      align-items: center;
      justify-content: center;
      padding: 0 $spacing-05;

      @include media("mobile") {
        padding: 0 $spacing-03;
      }

      @include media("tablet-min") {
        grid-template-columns: 1fr 250px 1fr;
      }
    }

    .page-nav-info {
      display: flex;
      justify-content: center;
      align-items: center;
      text-align: center;

      &.center {
        @include carbon--type-style("label-03", true);
        grid-column: 2/3;

        .bx--btn {
          width: 40px;
          justify-self: center;
        }
      }

      &:is(.left, .right) {
        @include carbon--type-style("label-01", true);
        @include line-clamp(1);
        @include media("mobile") {
          display: none;
        }

        .page-title {
          max-width: calc(100% - $spacing-12);
        }
      }

      &.left {
        @include media("tablet-min") {
          max-width: 188px;
        }

        @include media("desktop") {
          max-width: 225px;
        }

        @media screen and (min-width: 1920px) {
          max-width: 430px;
        }
      }

      &.center {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: $spacing-03;

        .nav-info {
          display: flex;
          justify-content: center;
          min-width: 130px;
          padding: 0 $spacing-05;
          gap: $spacing-05;
        }

        .page-label {
          justify-content: center;
          white-space: pre;
        }
      }
    }
  }

  &._condensed {
    .page-main {
      // Condensed page should not have scrollbar as the scroll will be on the document body
      // Overflow is unset to allow dropdown,tooltip,etc. content to be displayed fully
      overflow: unset;

      .main-wrapper {
        width: 100%;

        > .header {
          margin-left: auto;
          margin-right: auto;

          @include media("mobile") {
            width: calc(100% - #{$layout-01} - #{$layout-01});
          }

          @include media("tablet") {
            width: calc(100% - #{$layout-03} - #{$layout-03});
          }

          @include media("desktop") {
            width: calc(100% - #{$layout-05} - #{$layout-05});
          }
        }
      }
    }
  }

  &.drawer-collapsed {
    .page-side {
      // For the transition effect to occur
      @include media("tablet-max") {
        max-height: 0;
      }

      @include media("mobile-landscape", "desktop") {
        max-width: 0;

        .page-drawer {
          max-width: 0;
          // For the page drawer not to overlap with the content during the transition
          transform: translateX(20rem);
        }
      }
    }

    @include media("desktop") {
      .content-sticky {
        transform: translateX(calc(#{$page-side-width} / 2));
      }
    }
  }

  &.drawer-expanded {
    @include media("tablet-max") {
      .page-main {
        margin-bottom: 25vh !important;
      }
    }

    .page-main {
      @include media("mobile-landscape", "desktop") {
        max-width: calc(100% - #{$page-side-width});
      }
    }

    @include media("mobile-landscape") {
      .content-sticky {
        width: calc(#{$content-sticky-tablet-width} / 2);
        margin-left: calc(
          (
              100vw - (#{$content-sticky-tablet-width} / 2) - #{$page-side-width} +
                #{$collapsed-panel-width}
            ) /
            2
        );
      }
    }

    .back-to-top-button {
      @include media("tablet-max") {
        display: none;
      }
    }
  }

  &.error-page {
    min-height: inherit;
    display: flex;
    justify-content: center;
    align-items: center;

    .page-main {
      .main-wrapper {
        display: flex;
        flex-flow: column;
        justify-content: center;
        align-items: center;
      }

      .content {
        display: flex;
        flex-flow: column;
        justify-content: center;
        align-items: center;
        text-align: center;
        color: $text-02;

        @include media("desktop") {
          width: $comfort-reading-width;
        }
        @include media("tablet") {
          width: calc(100% - 128px - 128px);
        }
        @include media("mobile") {
          width: calc(100% - 16px - 16px);
        }

        h1 {
          @include carbon--type-style("productive-heading-06");
          margin: $spacing-07 0;
          color: $text-03;
        }

        h3,
        h4 {
          @include carbon--type-style("productive-heading-03");
          margin: $spacing-07 0;
          color: $text-01;
        }

        p {
          @include carbon--type-style("body-long-01", true);
          margin: $spacing-07 0;
        }
      }

      .action {
        > *:not(first-child) {
          margin-left: 0.5rem;
        }

        & + .action {
          margin-top: 1rem;
        }

        &:last-of-type {
          margin-bottom: 3rem;
        }
      }
    }

    .graphic {
      display: inline-flex;

      svg {
        @include media("mobile") {
          width: 120px;
          height: 120px;
        }
      }
    }
  }

  &.static-page {
    .back-to-top {
      @include carbon--type-style("button-01", true);
      display: inline-flex;
      align-items: center;
      padding: $spacing-03 $spacing-05;
      background-color: $ui-01;
      border-radius: 10px;
      text-transform: uppercase;

      .sls-icon {
        margin-right: $spacing-03;

        [stroke] {
          stroke: $icon-01;
        }
      }
    }

    .content {
      color: $text-01;

      h1 {
        @include carbon--type-style("productive-heading-06");
        color: $text-03;
      }

      h2 {
        @include carbon--type-style("productive-heading-05");
        color: $text-01;
      }

      h3 {
        @include carbon--type-style("productive-heading-04");
        color: $text-01;
      }

      h4 {
        @include carbon--type-style("productive-heading-03");
        color: $text-01;
      }

      h5 {
        @include carbon--type-style("productive-heading-02");
        color: $text-01;
      }

      h6 {
        @include carbon--type-style("productive-heading-01");
        color: $text-02;
      }

      h1,
      h2,
      h3,
      h4,
      h5,
      h6 {
        margin: $spacing-07 0 $spacing-05 0;

        &:first-child {
          margin: 0 0 $spacing-05 0;
        }
      }

      p {
        @include carbon--type-style("body-long-01", true);
        margin: $spacing-05 0;
      }

      ol,
      ul {
        &:first-child {
          margin-top: 0;
        }
      }

      ol {
        margin: $spacing-05 0;
        padding-left: $spacing-05;
        list-style-type: decimal;

        ol[type="a"] {
          padding-left: $spacing-08;
          list-style: lower-alpha;
        }

        ol[type="i"] {
          list-style: lower-roman;
        }

        &.lower-alpha {
          list-style-type: lower-alpha;
        }

        &.upper-alpha {
          list-style-type: upper-alpha;
        }

        &.lower-roman {
          list-style-type: lower-roman;
        }

        &.upper-roman {
          list-style-type: upper-roman;
        }
      }

      ul {
        margin: $spacing-05 0;
        padding-left: $spacing-05;
        list-style-type: disc;
      }

      li {
        margin: $spacing-05 0;
      }

      p,
      h2,
      h3 {
        &.u-heading {
          @include carbon--type-style("body-long-01", true);
          text-decoration: underline;
        }
        &.b-heading-2 {
          @include carbon--type-style("productive-heading-02");
          color: $text-01;
        }
      }

      .section-note {
        padding-left: $spacing-08;
      }
      .pointer-note {
        padding-left: $spacing-05;
      }
    }

    .page-main {
      .main-wrapper {
        margin-left: auto;
        margin-right: auto;
        padding-bottom: 8rem;

        > .header {
          margin: $spacing-07 0;
        }

        @include media("desktop") {
          width: $comfort-reading-width;
        }
        @include media("tablet") {
          max-width: $comfort-reading-width;
          width: calc(100% - 32px - 32px);
        }
        @include media("mobile") {
          width: calc(100% - 16px - 16px);
        }
      }
    }
  }

  &.page-notebook-editor-page {
    .page-main {
      .main-wrapper {
        .content-sticky {
          @include media("desktop") {
            margin-left: calc(((var(--page-main-width) + #{$collapsed-panel-width}) / 2));
            transform: translateX(-50%);
          }
        }
      }
    }
  }

  &.notebook-editor-cover {
    .page-main {
      .main-wrapper {
        .content-sticky {
          @include media("desktop") {
            margin-left: calc(((var(--page-main-width) - #{$collapsed-panel-width}) / 2));
            transform: translateX(-50%);
          }
        }
      }
    }
  }

  &.has-side {
    @include media("mobile-landscape", "desktop") {
      display: flex;
    }

    .page-main {
      .main-wrapper {
        // SLSSIT-11139: Set overflow:visible so that the dropdown menu won't be truncated when it goes out of the edge of the page
        overflow: visible;
      }
    }
  }

  &.page-nav-hidden {
    .page-nav {
      display: none;
    }
  }

  .page-main {
    position: relative;
    width: 100%;
    height: 100%;
    min-height: inherit;

    .attached-toolbar-wrapper {
      position: sticky;
      top: calc(5rem + (#{$global-banner-height} * (var(--banner-count, 0))));
      z-index: z-map("attached-toolbar");

      ._has-banner-action & {
        top: calc(
          5rem + #{$global-banner-height} * (var(--banner-count, 0) - 1) +
            #{$global-banner-sub-actions-height}
        );
      }

      @include media("mobile") {
        ._has-banner-sub-text & {
          top: calc(
            5rem + #{$global-banner-height} * (var(--banner-count, 0) - 1) +
              #{$global-banner-sub-text-height}
          );
        }
        ._has-banner-action & {
          top: calc(
            5rem + #{$global-banner-height} * (var(--banner-count, 0) - 1) +
              #{$global-banner-sub-actions-mobile-height}
          );
        }
      }

      .space-bar + .bx--content & {
        top: 8.5rem;
      }

      > div {
        position: absolute;
        right: $spacing-03;

        @for $i from 1 through 4 {
          &:nth-child(#{$i + 1}) {
            margin-top: calc(4 * #{$i}rem);
          }
        }
      }

      .meeting-link-toolbar {
        .icon-wrapper {
          display: inline-flex;

          .sls-icon {
            [stroke] {
              stroke: $icon-01;
            }
          }
        }
      }
    }

    .main-wrapper {
      display: flex;
      flex-flow: column;
      height: 100%;
      min-height: inherit;

      > .header {
        display: flex;
        align-items: center;
        margin: $spacing-07 0;
        flex-flow: row wrap;
        flex: 0 0 auto;

        @include media("mobile") {
          margin-top: $spacing-05;
          margin-bottom: $spacing-05;
        }

        + .content {
          margin-top: 0;
        }

        .header-wrapper {
          display: flex;
          align-items: center;
          flex-flow: row wrap;
          width: 100%;

          > .title {
            @include carbon--type-style("productive-heading-03", true);
            display: flex;
            flex: 1 1 100%;
            word-break: break-word;

            .sls-icon {
              flex: 0 0 auto;
              margin-right: $spacing-03;
            }
          }
        }

        .sub-title {
          @include carbon--type-style("body-long-01", true);
          flex: 1 1 100%;
          margin-top: $spacing-05;
        }
      }

      > .content {
        flex: 1 1 0%;
      }

      .content-sticky {
        max-width: calc(#{$main-content-width} - #{$page-side-width});

        @include media("desktop") {
          width: $comfort-reading-width;
          margin-left: calc(
            (
                var(--pagewidth) - #{$comfort-reading-width} - #{$page-side-width} -
                  #{$collapsed-panel-width}
              ) /
              2
          );

          @include media("main-content-min") {
            margin-left: calc(
              (
                  var(--pagewidth) - #{$comfort-reading-width} -
                    #{$page-side-width} - var(--panel-offset, 0)
                ) /
                2
            );
          }
        }
      }

      .content-floating-icons {
        position: fixed;
        width: var(--page-main-width);
        bottom: 0;
        display: flex;
        justify-content: flex-end;
        padding-right: $spacing-03;
        z-index: calc(#{z-map("page-nav")} + 1);

        :is(.btn-helpdesk-ticket, .back-to-top-button) {
          position: absolute;
        }
      }
    }
  }

  &.has-split-layout {
    &.content-page {
      padding-left: 0;
    }

    .page-main {
      .main-wrapper {
        > .content {
          margin-top: 0;
        }
      }
    }

    .layout-split {
      @include media("desktop") {
        display: flex;
        align-items: flex-start;
      }

      .layout-split-side {
        display: none;
        flex: 0 0 min($panel-drawer-width, 25vw);
        position: sticky;
        top: $app-header-height;
        height: calc(100vh - #{$app-header-height});
        padding: $spacing-05 $spacing-05 $spacing-09;
        background-color: $ui-background;
        overflow-y: auto;

        @include media("desktop") {
          display: block;
        }
      }

      .layout-split-main {
        flex: 1 1 auto;
        min-height: calc(100vh - #{$app-header-height});
        background-color: $ui-03;

        &-white {
          background-color: $ui-01;
        }

        @include media("desktop") {
          display: flex;
          flex-direction: column;
          padding-bottom: 0;
        }
      }
    }
  }

  .page-nav {
    &:empty {
      display: none;
    }
  }

  .btn-helpdesk-ticket {
    width: fit-content !important;
    position: fixed;
    right: 0.5rem;
    bottom: 0.5rem;
    z-index: calc(#{z-map("page-nav")} + 1);

    .bx--btn {
      max-width: 48px;
      min-width: 48px;

      &:focus,
      &:active {
        background-color: $hover-primary;
      }

      span {
        display: flex;
        flex-flow: column;
        justify-content: center;
        align-items: center;
        @include carbon--type-style("button-02");

        .sls-icon {
          margin-right: 0;
        }

        .label {
          margin-top: -$spacing-01;
        }
      }
    }

    & ~ .back-to-top-button {
      bottom: 4rem;
    }
  }
}

.page-side {
  transition: all $duration--moderate-02 ease-in-out;

  @include media("tablet-max") {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    max-height: calc((var(--vh, 1vh) * 55));
    box-shadow: $shadow-04;
    border-top: 1px solid $ui-04;
    z-index: -1;
    background-color: $ui-03;

    .drawer-expanded & {
      z-index: z-map("page-drawer");
    }

    .drawer-side-expand & {
      left: auto;
      top: calc(#{$app-header-height} + (#{$global-banner-height} * var(--banner-count, 0)));
      display: flex;
      justify-content: center;
      width: $page-side-width;
      max-height: unset;
      border-top: 0;
      overflow-y: unset;

      .space-bar.visiting-view + .app-content & {
        top: calc(#{$app-header-height} + #{$space-bar-visiting-height});
      }
    }

    .drawer-side-expand.is-game-enabled & {
      top: calc(
        #{$app-header-height} + (#{$global-banner-height} * var(--banner-count, 0)) +
          #{$achievement-bar-height}
      );
    }
  }

  @include media("tablet") {
    // To account for the side panel
    left: $collapsed-panel-width;
    .page-sub-content {
      padding-left: $spacing-03;
    }
  }

  @include media("mobile-landscape", "desktop") {
    position: static;
    display: flex;
    justify-content: center;
    flex: 0 0 #{$page-side-width};
    max-width: $page-side-width;
    max-height: unset;
    height: auto;
    overflow-y: auto;
    padding-bottom: $spacing-05; // using padding as the background color got cut off
    z-index: z-map("default");
  }

  .page-drawer {
    @include media("mobile-landscape", "desktop") {
      flex: 0 0 100%;
      height: calc((var(--vh, 1vh) * 100) - #{$app-header-height});
      position: fixed;
      width: $panel-drawer-width;
      background-color: $ui-03;
      overflow-y: auto;
      transform: none;
    }

    ._has-space-bar & {
      height: calc((var(--vh, 1vh) * 100) - #{$app-header-height} - #{$space-bar-visiting-height});
    }
  }
}
</style>
